import { useRef, useState } from 'react';
import {
  Plus,
  Upload,
  Download,
  Pencil,
  X,
  Search,
  Square,
  Image as ImageIcon,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import type { ComponentTemplate } from '../types';
import { useLibrary } from '../libraryStore';
import { makeBlankTemplate, parseLibraryTemplates } from '../library';
import { LibraryImportModal } from './LibraryImportModal';
import { ComponentEditor } from './ComponentEditor';
import { downloadText } from '../exports';
import { useStore, type MechanicalItemData } from '../store';
import { fitImageSize, readClipboardImage } from '../clipboardImages';
import { requirePro, useIsPro } from '../entitlements';
import { showToast } from './Toast';

function groupByCategory(items: ComponentTemplate[]) {
  const map = new Map<string, ComponentTemplate[]>();
  for (const it of items) {
    if (!map.has(it.category)) map.set(it.category, []);
    map.get(it.category)!.push(it);
  }
  return Array.from(map.entries());
}

export function Palette({
  collapsed,
  onToggleCollapsed,
}: {
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
} = {}) {
  const templates = useLibrary((s) => s.templates);
  const upsert = useLibrary((s) => s.upsert);
  const remove = useLibrary((s) => s.remove);
  const exportJson = useLibrary((s) => s.exportJson);
  const addImage = useStore((s) => s.addImage);
  const isPro = useIsPro();
  const addMechanicalItem = useStore((s) => s.addMechanicalItem);
  const syncTemplateInstances = useStore((s) => s.syncTemplateInstances);
  const lastPastePosition = useStore((s) => s.lastPastePosition);
  const addRequirement = useStore((s) => s.addRequirement);
  const activeSheetKind = useStore(
    (s) => s.sheets.find((sh) => sh.id === s.activeSheetId)?.kind ?? 'schematic',
  );
  const isBlockSheet = activeSheetKind === 'block';
  const isMechanicalSheet = activeSheetKind === 'mechanical';
  const isRequirementsSheet = activeSheetKind === 'requirements';
  const isInterconnectSheet = activeSheetKind === 'interconnect';
  const isDocumentSheet = activeSheetKind === 'document';
  const addBundle = useStore((s) => s.addBundle);

  const [editing, setEditing] = useState<ComponentTemplate | null>(null);
  const [importPreview, setImportPreview] = useState<ComponentTemplate[] | null>(null);
  const [measuredBaseOpen, setMeasuredBaseOpen] = useState(false);
  const [search, setSearch] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const q = search.trim().toLowerCase();
  const filteredTemplates = q
    ? templates.filter((t) => {
        const blob = [t.label, t.category, t.partNumber ?? '', t.manufacturer ?? '']
          .join(' ')
          .toLowerCase();
        return blob.includes(q);
      })
    : templates;
  const groups = groupByCategory(filteredTemplates);
  const mechanicalTemplates = filteredTemplates.filter(
    (t) => t.mechanical?.widthMm && t.mechanical?.heightMm,
  );

  const saveTemplate = (template: ComponentTemplate) => {
    const previousTemplate = useLibrary.getState().templates.find((t) => t.key === template.key);
    upsert(template);
    const r = syncTemplateInstances(template, previousTemplate);
    setEditing(null);
    const updated = r.components + r.mechanicalItems;
    if (updated > 0) {
      const parts = [`${updated} placed item${updated === 1 ? '' : 's'} updated`];
      if (r.removedEdges > 0)
        parts.push(`${r.removedEdges} wire${r.removedEdges === 1 ? '' : 's'} removed (pins changed)`);
      showToast(`Saved to library · ${parts.join(' · ')}`, r.removedEdges > 0 ? 'info' : 'success');
    } else {
      showToast('Saved to library', 'success');
    }
  };

  const onExport = () => {
    downloadText(`skema-library.json`, exportJson());
  };

  const onImportClick = () => fileRef.current?.click();

  const onImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        // Parse only — the preview modal shows the diff and merges on approval.
        setImportPreview(parseLibraryTemplates(String(reader.result)));
      } catch (err) {
        alert(`Import failed: ${(err as Error).message}`);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const addMechanicalTemplate = (template: ComponentTemplate) => {
    const mechanical = template.mechanical;
    if (!mechanical?.widthMm || !mechanical?.heightMm) return;
    addMechanicalItem(lastPastePosition ?? { x: 120, y: 120 }, {
      label: template.label,
      templateKey: template.key,
      partNumber: template.partNumber,
      manufacturer: template.manufacturer,
      color: template.color ?? '#64748b',
      ...mechanical,
      scalePxPerMm: 1,
    });
  };

  const addMeasuredBase = () => {
    setMeasuredBaseOpen(true);
  };

  const pasteImageFromClipboard = async () => {
    if (!requirePro('Adding photos')) return;
    const image = await readClipboardImage();
    if (!image) {
      alert('Clipboard does not contain an image.');
      return;
    }
    addImage(lastPastePosition ?? { x: 120, y: 120 }, image.src, fitImageSize(image), image.name);
  };

  const onDragStart = (e: React.DragEvent, key: string) => {
    e.dataTransfer.setData('application/skema-component', key);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Palette is rendered inside the Sidebar (which provides the outer width
  // and border). We only fill the remaining vertical space here.
  const contentStyle: React.CSSProperties = {
    flex: 1,
    minHeight: 0,
    background: 'var(--panel)',
    display: 'flex',
    flexDirection: 'column',
  };

  const bannerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 12px',
    background: 'linear-gradient(var(--chrome-top), var(--chrome-bottom))',
    borderBottom: '1px solid var(--chrome-border)',
    height: 28,
    boxSizing: 'border-box',
    position: 'relative',
  };

  const bannerLabelStyle: React.CSSProperties = {
    fontWeight: 800,
    fontSize: 10.5,
    letterSpacing: 0.8,
    color: 'var(--text)',
    textTransform: 'uppercase',
    paddingLeft: 6,
    borderLeft: '3px solid var(--accent)',
    marginLeft: -6,
  };

  /** Section title bar with an optional accordion collapse chevron. */
  const banner = (label: string) => (
    <div style={bannerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
        {onToggleCollapsed && (
          <button
            onClick={onToggleCollapsed}
            title={collapsed ? 'Expand section' : 'Collapse section'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 16,
              height: 16,
              border: 'none',
              background: 'transparent',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            {collapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
          </button>
        )}
        <div style={bannerLabelStyle}>{label}</div>
      </div>
    </div>
  );

  // Document sheet: compact side panel — document editing happens in the centre.
  if (isDocumentSheet) {
    return (
      <div style={contentStyle}>
        {banner('Document')}
        <div
          style={{
            padding: 14,
            color: 'var(--text-muted)',
            fontSize: 12,
            lineHeight: 1.6,
          }}
        >
          <p style={{ marginTop: 0 }}>
            <strong style={{ color: 'var(--text)' }}>Document sheet</strong> — write
            free-form text such as installation guides, maintenance manuals, or
            commissioning checklists. Pick a category in the toolbar and add as
            many sections as you need.
          </p>
          <p style={{ marginBottom: 0 }}>
            Tip: each section can hold multi-line plain text. Use the up / down
            arrows on a section card to reorder.
          </p>
        </div>
      </div>
    );
  }

  // Interconnect sheet: compact side panel for visual harness drawing pages.
  if (isInterconnectSheet) {
    return (
      <div style={contentStyle}>
        {banner('Harnesses')}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: '5px 8px',
            background: 'var(--panel-alt)',
            borderBottom: '1px solid var(--panel-border)',
          }}
        >
          <PaletteIconBtn
            icon={<Plus size={15} />}
            label="Add harness page"
            onClick={() => addBundle()}
            accent
          />
        </div>
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 14,
            fontSize: 12,
            color: 'var(--text-muted)',
            lineHeight: 1.55,
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
            Harness pages
          </div>
          <p style={{ margin: '0 0 12px 0' }}>
            Each harness is shown as a text/table page: one card per
            connector with its auto-derived pinout table (termination and
            contact PN are editable).
          </p>
          <p style={{ margin: '0 0 12px 0' }}>
            Keep assigning wires from the schematic Inspector — the
            connector list on the harness page stays in sync automatically,
            no extra step needed.
          </p>
          <p style={{ display: 'none', margin: '0 0 12px 0' }}>
            List the harnesses that connect subsystems (Robot ↔ Control box etc.).
            Each bundle captures the cables in it, the conduit, and how it enters
            each unit.
          </p>
          <p style={{ display: 'none', margin: '0 0 12px 0' }}>
            Use this together with the schematic sheets — schematic shows the
            internal pin-to-pin wiring, while this sheet documents the physical
            cable harness running between cabinets.
          </p>
        </div>
        <div
          style={{
            padding: '10px 14px',
            borderTop: '1px solid var(--panel-border)',
            fontSize: 10,
            color: 'var(--text-dim)',
            lineHeight: 1.5,
            background: 'var(--panel-alt)',
          }}
        >
          Harness pages use the same schematic harness assignments.
        </div>
      </div>
    );
  }

  // Requirements sheet: compact side panel with category legend and quick add.
  if (isRequirementsSheet) {
    return (
      <div style={contentStyle}>
        {banner('Requirements')}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: '5px 8px',
            background: 'var(--panel-alt)',
            borderBottom: '1px solid var(--panel-border)',
          }}
        >
          <PaletteIconBtn
            icon={<Plus size={15} />}
            label="Add requirement"
            onClick={() => addRequirement()}
            accent
          />
        </div>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 14,
            fontSize: 12,
            color: 'var(--text-muted)',
            lineHeight: 1.55,
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
            Project requirements
          </div>
          <p style={{ margin: '0 0 12px 0' }}>
            Capture what the system must do, how well, and how you'll verify it.
          </p>
          <div
            style={{
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              color: 'var(--text-dim)',
              marginBottom: 6,
              fontWeight: 700,
            }}
          >
            Categories
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11 }}>
            <li><strong>Functional</strong> — what the system does</li>
            <li><strong>Performance</strong> — speed, load, torque, accuracy</li>
            <li><strong>Safety</strong> — E-stop, PL/SIL, redundancy</li>
            <li><strong>Interface</strong> — I/O, comms, protocols</li>
            <li><strong>Environmental</strong> — IP, temp, EMC</li>
            <li><strong>Mechanical</strong> — enclosure, mounting, weight</li>
          </ul>
          <div
            style={{
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              color: 'var(--text-dim)',
              marginTop: 14,
              marginBottom: 6,
              fontWeight: 700,
            }}
          >
            Priority (MoSCoW)
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11 }}>
            <li><strong style={{ color: '#dc2626' }}>Must</strong> — critical for v1</li>
            <li><strong style={{ color: '#f59e0b' }}>Should</strong> — important, not critical</li>
            <li><strong style={{ color: '#2563eb' }}>Could</strong> — nice to have</li>
            <li><strong style={{ color: '#64748b' }}>Won't</strong> — out of scope</li>
          </ul>
        </div>

        <div
          style={{
            padding: '10px 14px',
            borderTop: '1px solid var(--panel-border)',
            fontSize: 10,
            color: 'var(--text-dim)',
            lineHeight: 1.5,
            background: 'var(--panel-alt)',
          }}
        >
          Requirements are included as a sheet in the Spec .xlsx export.
        </div>
      </div>
    );
  }

  // Block-diagram-only palette: just block and sticky-note tools.
  if (isBlockSheet) {
    return (
      <div style={contentStyle}>
        {banner('Blocks')}

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 14,
            fontSize: 12,
            color: 'var(--text-muted)',
            lineHeight: 1.55,
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
            Block diagram
          </div>
          <p style={{ margin: '0 0 10px 0' }}>
            Sketch the high-level architecture here. Each block represents a module or
            subsystem (e.g. <em>Power</em>, <em>Motion Control</em>, <em>Vision</em>).
          </p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>Add a block from <strong>Insert ▸ Block</strong> (toolbar) or the left tool rail, then drag it to position.</li>
            <li>Double-click a block to edit its title and subtitle.</li>
            <li>Drag a handle on any side of a block to connect to another block.</li>
            <li>Switch to the <strong>Schematic</strong> sheet for detailed component wiring.</li>
          </ul>
        </div>

        <div
          style={{
            padding: '10px 14px',
            borderTop: '1px solid var(--panel-border)',
            fontSize: 10,
            color: 'var(--text-dim)',
            lineHeight: 1.5,
            background: 'var(--panel-alt)',
          }}
        >
          High-level overview sheet. Pins and wiring live in the Schematic sheet.
        </div>
      </div>
    );
  }

  if (isMechanicalSheet) {
    return (
      <>
        <div style={contentStyle}>
          {banner('Mechanical Layout')}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              padding: '5px 8px',
              background: 'var(--panel-alt)',
              borderBottom: '1px solid var(--panel-border)',
            }}
          >
            <PaletteIconBtn
              icon={<Square size={15} />}
              label="Add measured base / chassis"
              onClick={addMeasuredBase}
              accent
            />
            <PaletteIconBtn
              icon={<Plus size={15} />}
              label="New library component with mechanical dimensions"
              onClick={() => setEditing(makeBlankTemplate())}
            />
            <PaletteDivider />
            <PaletteIconBtn
              icon={<ImageIcon size={15} />}
              label={
                isPro
                  ? 'Paste reference image from clipboard'
                  : 'Paste reference image from clipboard (Pro)'
              }
              onClick={() => void pasteImageFromClipboard()}
            />
            <input
              ref={fileRef}
              type="file"
              accept=".json,application/json"
              style={{ display: 'none' }}
              onChange={onImportFile}
            />
          </div>

          <div
            style={{
              padding: '6px 8px',
              borderBottom: '1px solid var(--panel-border)',
              position: 'relative',
              background: 'var(--panel-alt)',
            }}
          >
            <Search
              size={13}
              style={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-muted)',
                pointerEvents: 'none',
              }}
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search measured components..."
              style={{
                width: '100%',
                background: 'var(--panel)',
                color: 'var(--text)',
                border: '1px solid var(--panel-border)',
                borderRadius: 4,
                padding: '5px 9px 5px 26px',
                fontSize: 12,
                outline: 'none',
                boxSizing: 'border-box',
                height: 26,
              }}
            />
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 8px 12px' }}>
            <div
              style={{
                fontSize: 10,
                color: 'var(--text-dim)',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                fontWeight: 700,
                margin: '2px 4px 8px',
              }}
            >
              Measured library components
            </div>
            {mechanicalTemplates.length === 0 ? (
              <div
                style={{
                  margin: 6,
                  padding: 14,
                  border: '1px dashed var(--panel-border-strong)',
                  borderRadius: 'var(--radius)',
                  background: 'var(--panel-alt)',
                  color: 'var(--text-muted)',
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                No measured components yet. Open a library component and fill
                Width / Height in the Mechanical section.
              </div>
            ) : (
              mechanicalTemplates.map((template) => (
                <MechanicalTemplateCard
                  key={template.key}
                  template={template}
                  onPlace={() => addMechanicalTemplate(template)}
                  onEdit={() => setEditing(template)}
                />
              ))
            )}
          </div>

          <div
            style={{
              padding: '10px 14px',
              borderTop: '1px solid var(--panel-border)',
              fontSize: 10,
              color: 'var(--text-dim)',
              lineHeight: 1.5,
              background: 'var(--panel-alt)',
            }}
          >
            Layout scale: 1 px = 1 mm. Resize a placed item to update its mm footprint.
          </div>
        </div>

        {editing && (
          <ComponentEditor
            template={editing}
            onCancel={() => setEditing(null)}
            onSave={(t) => {
              saveTemplate(t);
            }}
          />
        )}
        {measuredBaseOpen && (
          <MeasuredBaseModal
            onCancel={() => setMeasuredBaseOpen(false)}
            onSubmit={(base) => {
              addMechanicalItem(lastPastePosition ?? { x: 80, y: 80 }, base);
              setMeasuredBaseOpen(false);
            }}
          />
        )}
        {importPreview && (
          <LibraryImportModal incoming={importPreview} onClose={() => setImportPreview(null)} />
        )}
      </>
    );
  }

  // Schematic palette: full component library + pin-level tooling.
  return (
    <>
      <div style={contentStyle}>
        {banner('Library')}

        {/* Icon action toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: '5px 8px',
            background: 'var(--panel-alt)',
            borderBottom: '1px solid var(--panel-border)',
          }}
        >
          <PaletteIconBtn
            icon={<Plus size={15} />}
            label="New component"
            onClick={() => setEditing(makeBlankTemplate())}
          />
          <PaletteDivider />
          <PaletteIconBtn
            icon={<Upload size={15} />}
            label="Import library JSON"
            onClick={onImportClick}
          />
          <PaletteIconBtn
            icon={<Download size={15} />}
            label="Export library JSON"
            onClick={onExport}
          />
          <input
            ref={fileRef}
            type="file"
            accept=".json,application/json"
            style={{ display: 'none' }}
            onChange={onImportFile}
          />
        </div>

        {/* Search */}
        <div
          style={{
            padding: '6px 8px',
            borderBottom: '1px solid var(--panel-border)',
            position: 'relative',
          }}
        >
          <Search
            size={13}
            style={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              pointerEvents: 'none',
            }}
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search components…"
            style={{
              width: '100%',
              background: 'var(--panel)',
              color: 'var(--text)',
              border: '1px solid var(--panel-border)',
              borderRadius: 4,
              padding: '5px 9px 5px 26px',
              fontSize: 12,
              outline: 'none',
              boxSizing: 'border-box',
              height: 26,
            }}
          />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 8px 12px' }}>
          {templates.length === 0 && (
            <div
              style={{
                margin: 6,
                padding: 18,
                border: '1px dashed var(--panel-border-strong)',
                borderRadius: 'var(--radius)',
                fontSize: 12,
                color: 'var(--text-muted)',
                textAlign: 'center',
                background: 'var(--panel-alt)',
              }}
            >
              No components yet.
              <br />
              Click <strong style={{ color: 'var(--text)' }}>+ New</strong> to create one.
            </div>
          )}
          {templates.length > 0 && filteredTemplates.length === 0 && (
            <div
              style={{
                margin: 6,
                padding: 14,
                fontSize: 12,
                color: 'var(--text-muted)',
                textAlign: 'center',
              }}
            >
              No matches for “{search}”.
            </div>
          )}

          {groups.map(([category, items]) => (
            <div key={category} style={{ marginBottom: 10 }}>
              <div
                style={{
                  fontSize: 10,
                  color: 'var(--text-dim)',
                  textTransform: 'uppercase',
                  padding: '8px 6px 4px',
                  letterSpacing: 0.6,
                  fontWeight: 700,
                }}
              >
                {category}
              </div>
              {items.map((item) => (
                <div
                  key={item.key}
                  draggable
                  onDragStart={(e) => onDragStart(e, item.key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 6px 6px 0',
                    margin: '2px 0',
                    background: 'var(--panel)',
                    borderRadius: 4,
                    cursor: 'grab',
                    userSelect: 'none',
                    border: '1px solid var(--panel-border)',
                    transition: 'all 0.12s',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-canvas)';
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--panel-border-strong)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background = 'var(--panel)';
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--panel-border)';
                  }}
                >
                  <div
                    style={{
                      width: 3,
                      alignSelf: 'stretch',
                      background: item.color ?? '#64748b',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0, paddingLeft: 6 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 12,
                        color: 'var(--text)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.label}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: 'var(--text-muted)',
                        marginTop: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.partNumber ? `${item.partNumber} · ` : ''}
                      {item.pins.length} pins
                    </div>
                  </div>
                  <button
                    style={iconBtnStyle}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditing(item);
                    }}
                    draggable={false}
                    onDragStart={(e) => e.stopPropagation()}
                    title="Edit"
                  >
                    <Pencil size={12} />
                  </button>
                  <button
                    style={{ ...iconBtnStyle, color: 'var(--danger)' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm(`Delete component "${item.label}" from library?`)) {
                        remove(item.key);
                      }
                    }}
                    draggable={false}
                    onDragStart={(e) => e.stopPropagation()}
                    title="Delete from library"
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div
          style={{
            padding: '10px 14px',
            borderTop: '1px solid var(--panel-border)',
            fontSize: 10,
            color: 'var(--text-dim)',
            lineHeight: 1.5,
            background: 'var(--panel-alt)',
          }}
        >
          Saved in this browser. Shared across all projects.
        </div>
      </div>

      {editing && (
        <ComponentEditor
          template={editing}
          onSave={(t) => {
            saveTemplate(t);
          }}
          onCancel={() => setEditing(null)}
        />
      )}
      {importPreview && (
        <LibraryImportModal incoming={importPreview} onClose={() => setImportPreview(null)} />
      )}
    </>
  );
}

function PaletteIconBtn({
  icon,
  label,
  onClick,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  accent?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 26,
        height: 24,
        padding: 0,
        background: accent ? 'var(--accent-soft)' : 'transparent',
        color: accent ? 'var(--accent)' : 'var(--text)',
        border: accent ? '1px solid #bfdbfe' : '1px solid transparent',
        borderRadius: 4,
        cursor: 'pointer',
        transition: 'all 0.12s',
      }}
      onMouseEnter={(e) => {
        if (!accent) e.currentTarget.style.background = 'var(--panel-border)';
      }}
      onMouseLeave={(e) => {
        if (!accent) e.currentTarget.style.background = 'transparent';
      }}
    >
      {icon}
    </button>
  );
}

function PaletteDivider() {
  return (
    <div
      style={{
        width: 1,
        height: 16,
        background: 'var(--panel-border)',
        margin: '0 3px',
      }}
    />
  );
}

/** Exported so the LeftToolDock can offer the same quick-add on mechanical
 *  sheets without the user having to open the Library tab. */
export function MeasuredBaseModal({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void;
  onSubmit: (base: MechanicalItemData) => void;
}) {
  const [label, setLabel] = useState('Robot chassis');
  const [widthMm, setWidthMm] = useState(800);
  const [heightMm, setHeightMm] = useState(500);
  const [depthMm, setDepthMm] = useState<number | ''>('');
  const [color, setColor] = useState('#f3e2c2');
  const [scalePxPerMm, setScalePxPerMm] = useState(0.5);

  const submit = () => {
    if (!label.trim() || widthMm <= 0 || heightMm <= 0) return;
    onSubmit({
      label: label.trim(),
      widthMm,
      heightMm,
      depthMm: depthMm === '' ? undefined : depthMm,
      mounting: 'chassis',
      color,
      scalePxPerMm,
      notes: 'Reference base / chassis',
    });
  };

  return (
    <div
      onMouseDown={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(15, 23, 42, 0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: 360,
          background: 'var(--panel)',
          color: 'var(--text)',
          border: '1px solid var(--panel-border)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-lg)',
          padding: 16,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>
          Add measured base / chassis
        </div>
        <div style={{ display: 'grid', gap: 8 }}>
          <ModalField label="Name">
            <input value={label} onChange={(e) => setLabel(e.target.value)} style={modalInput} />
          </ModalField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <ModalField label="Width (mm)">
              <input
                type="number"
                min={1}
                value={widthMm}
                onChange={(e) => setWidthMm(Number(e.target.value) || 1)}
                style={modalInput}
              />
            </ModalField>
            <ModalField label="Height (mm)">
              <input
                type="number"
                min={1}
                value={heightMm}
                onChange={(e) => setHeightMm(Number(e.target.value) || 1)}
                style={modalInput}
              />
            </ModalField>
            <ModalField label="Depth (mm)">
              <input
                type="number"
                min={0}
                value={depthMm}
                onChange={(e) =>
                  setDepthMm(e.target.value === '' ? '' : Number(e.target.value) || 0)
                }
                style={modalInput}
              />
            </ModalField>
            <ModalField label="Scale px/mm">
              <input
                type="number"
                min={0.1}
                step={0.1}
                value={scalePxPerMm}
                onChange={(e) => setScalePxPerMm(Math.max(0.1, Number(e.target.value) || 1))}
                style={modalInput}
              />
            </ModalField>
          </div>
          <ModalField label="Color">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              style={{ ...modalInput, padding: 2, height: 32 }}
            />
          </ModalField>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button onClick={onCancel} style={modalSecondaryBtn}>
            Cancel
          </button>
          <button onClick={submit} style={modalPrimaryBtn}>
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function MechanicalTemplateCard({
  template,
  onPlace,
  onEdit,
}: {
  template: ComponentTemplate;
  onPlace: () => void;
  onEdit: () => void;
}) {
  const m = template.mechanical;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '22px 1fr auto',
        gap: 8,
        alignItems: 'center',
        padding: '7px 8px',
        marginBottom: 6,
        border: '1px solid var(--panel-border)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--panel)',
      }}
    >
      <button
        onClick={onPlace}
        title="Place on mechanical layout"
        style={{
          width: 22,
          height: 22,
          borderRadius: 4,
          border: '1px solid var(--panel-border)',
          background: template.color ?? '#64748b',
          cursor: 'pointer',
        }}
      />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--text)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {template.label}
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
          {m?.widthMm} x {m?.heightMm} mm
          {m?.depthMm ? ` x ${m.depthMm}` : ''} · {m?.mounting ?? 'free'}
        </div>
        {template.partNumber && (
          <div style={{ fontSize: 9, color: 'var(--text-dim)', marginTop: 1 }}>
            {template.partNumber}
          </div>
        )}
      </div>
      <button onClick={onEdit} style={iconBtnStyle} title="Edit library component">
        <Pencil size={12} />
      </button>
    </div>
  );
}

const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  color: 'var(--text-muted)',
  border: 'none',
  borderRadius: 4,
  padding: '3px 5px',
  cursor: 'pointer',
  lineHeight: 1,
  flexShrink: 0,
  height: 22,
};

const modalInput: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border)',
  borderRadius: 5,
  padding: '6px 8px',
  fontSize: 12,
};

const modalPrimaryBtn: React.CSSProperties = {
  background: 'var(--accent)',
  color: 'white',
  border: 'none',
  borderRadius: 5,
  padding: '6px 14px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
};

const modalSecondaryBtn: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--panel-border)',
  borderRadius: 5,
  padding: '6px 12px',
  fontSize: 12,
  cursor: 'pointer',
};
